<template>
  <div class="date-picker-wrapper">
    <div class="date-input-container" @click="toggleCalendar">
      <input 
        type="text" 
        :value="formattedDate" 
        readonly 
        class="date-input"
        :placeholder="placeholder"
      >
      <span class="calendar-icon">📅</span>
    </div>
    
    <div v-if="showCalendar" class="calendar-dropdown" ref="calendar">
      <div class="calendar-header">
        <button @click="prevMonth" class="nav-btn">&lt;</button>
        <div class="month-year">{{ currentMonthYear }}</div>
        <button @click="nextMonth" class="nav-btn">&gt;</button>
      </div>
      
      <div class="weekdays">
        <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
      </div>
      
      <div class="calendar-grid">
        <div 
          v-for="day in calendarDays" 
          :key="day.date"
          :class="['calendar-day', 
                  { 'disabled': day.disabled }, 
                  { 'selected': day.selected }, 
                  { 'today': day.isToday }]"
          @click="selectDate(day)"
        >
          {{ day.day }}
        </div>
      </div>
      
      <div class="calendar-footer">
        <button @click="selectToday" class="today-btn">今天</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'DatePicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '选择日期'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const showCalendar = ref(false)
    const currentDate = ref(new Date())
    const selectedDate = ref(props.modelValue ? new Date(props.modelValue) : null)
    
    const weekdays = ['日', '一', '二', '三', '四', '五', '六']
    
    // 计算当前月份和年份
    const currentMonthYear = computed(() => {
      return currentDate.value.toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: 'long' 
      })
    })
    
    // 计算格式化日期
    const formattedDate = computed(() => {
      if (!selectedDate.value) return ''
      // 修复时区问题，使用getFullYear等方法获取正确的日期
      const year = selectedDate.value.getFullYear();
      const month = String(selectedDate.value.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })
    
    // 计算日历天数
    const calendarDays = computed(() => {
      const year = currentDate.value.getFullYear()
      const month = currentDate.value.getMonth()
      
      // 本月第一天
      const firstDay = new Date(year, month, 1)
      // 本月最后一天
      const lastDay = new Date(year, month + 1, 0)
      // 上个月最后一天
      const prevLastDay = new Date(year, month, 0)
      
      const days = []
      
      // 添加上个月的日期
      const prevDaysCount = firstDay.getDay()
      for (let i = prevDaysCount - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevLastDay.getDate() - i)
        // 使用本地日期格式避免时区问题
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        days.push({
          date: dateStr,
          day: date.getDate(),
          disabled: true
        })
      }
      
      // 添加本月的日期
      const today = new Date()
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(year, month, i)
        // 使用本地日期格式避免时区问题
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        days.push({
          date: dateStr,
          day: i,
          disabled: false,
          selected: selectedDate.value && dateStr === `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`,
          isToday: date.toDateString() === today.toDateString()
        })
      }
      
      // 添加下个月的日期
      const totalCells = 42 // 6行7列
      const nextDaysCount = totalCells - days.length
      for (let i = 1; i <= nextDaysCount; i++) {
        const date = new Date(year, month + 1, i)
        // 使用本地日期格式避免时区问题
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        days.push({
          date: dateStr,
          day: i,
          disabled: true
        })
      }
      
      return days
    })
    
    // 切换日历显示
    const toggleCalendar = () => {
      showCalendar.value = !showCalendar.value
    }
    
    // 选择日期
    const selectDate = (day) => {
      if (day.disabled) return
      
      // 直接使用day.date确保不有时区问题
      selectedDate.value = new Date(day.date + 'T00:00:00');
      emit('update:modelValue', day.date)
      showCalendar.value = false
    }
    
    // 选择今天
    const selectToday = () => {
      const today = new Date();
      // 获取本地日期而不受时区影响
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // 使用ISO格式确保日期正确
      selectedDate.value = new Date(dateString + 'T00:00:00');
      emit('update:modelValue', dateString)
      showCalendar.value = false
    }
    
    // 上一个月
    const prevMonth = () => {
      currentDate.value.setMonth(currentDate.value.getMonth() - 1)
      currentDate.value = new Date(currentDate.value)
    }
    
    // 下一个月
    const nextMonth = () => {
      currentDate.value.setMonth(currentDate.value.getMonth() + 1)
      currentDate.value = new Date(currentDate.value)
    }
    
    // 点击外部关闭日历
    const handleClickOutside = (event) => {
      if (showCalendar.value) {
        const calendar = document.querySelector('.calendar-dropdown')
        if (calendar && !calendar.contains(event.target) && 
            !event.target.closest('.date-input-container')) {
          showCalendar.value = false
        }
      }
    }
    
    // 挂载时添加事件监听器
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })
    
    // 卸载前移除事件监听器
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
    })
    
    return {
      showCalendar,
      currentDate,
      selectedDate,
      weekdays,
      currentMonthYear,
      formattedDate,
      calendarDays,
      toggleCalendar,
      selectDate,
      selectToday,
      prevMonth,
      nextMonth
    }
  }
}
</script>

<style scoped>
.date-picker-wrapper {
  position: relative;
  width: 100%;
}

.date-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.date-input {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.5);
  background: rgba(26, 13, 45, 0.7);
  color: white;
  font-size: 1rem;
  cursor: pointer;
}

.date-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 8px rgba(102, 126, 234, 0.6);
}

.date-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.calendar-icon {
  position: absolute;
  right: 12px;
  color: #ffcc00;
  pointer-events: none;
}

.calendar-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(13, 26, 45, 0.95);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(102, 126, 234, 0.5);
  z-index: 1000;
  margin-top: 5px;
  padding: 15px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.nav-btn {
  background: rgba(102, 126, 234, 0.2);
  border: none;
  color: #ffcc00;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
}

.nav-btn:hover {
  background: rgba(102, 126, 234, 0.4);
  transform: scale(1.1);
}

.month-year {
  color: #ffcc00;
  font-weight: bold;
  font-size: 1.1rem;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  margin-bottom: 10px;
}

.weekday {
  text-align: center;
  color: #667eea;
  font-weight: bold;
  padding: 8px 0;
  font-size: 0.9rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
}

.calendar-day {
  text-align: center;
  padding: 10px 0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.calendar-day:hover:not(.disabled) {
  background: rgba(102, 126, 234, 0.3);
  color: white;
}

.calendar-day.selected {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: bold;
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
}

.calendar-day.today {
  background: rgba(255, 204, 0, 0.2);
  color: #ffcc00;
  font-weight: bold;
}

.calendar-day.today.selected {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.calendar-day.disabled {
  color: rgba(255, 255, 255, 0.3);
  cursor: not-allowed;
}

.calendar-footer {
  margin-top: 10px;
  text-align: center;
}

.today-btn {
  background: rgba(255, 204, 0, 0.2);
  border: 1px solid rgba(255, 204, 0, 0.5);
  color: #ffcc00;
  padding: 6px 12px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.today-btn:hover {
  background: rgba(255, 204, 0, 0.3);
  transform: translateY(-2px);
}
</style>